// ═══════════════════════════════════════════════════════════════════
//  modules/sanctions/SanctionFiche.js
//  Le détail d'une sanction : ce que le joueur a lu, ce que le staff
//  a noté, et tout ce qui lui est arrivé depuis.
//  Les droits viennent du backend — le bouton n'apparaît pas si
//  l'action est refusée, et le backend la refuserait de toute façon.
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { THEME } from "../../socle/theme";
import { dateCourte, dureeLisible, GRADE_LABEL, NIVEAU_STYLE, STATUT_STYLE, tempsRestant } from "./constantes";

export function SanctionFiche({ api, id, bareme, onRetour, onChange }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [note, setNote] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [action, setAction] = useState(null);      // 'lever' | 'duree'
  const [motifAction, setMotifAction] = useState("");
  const [nouvelleDuree, setNouvelleDuree] = useState("");

  const charger = useCallback(async () => {
    try {
      const r = await api.fiche(id);
      setD(r);
      setNote(r.sanction.note_interne || "");
      setErreur(null);
    } catch (e) { setErreur(e.message); }
  }, [api, id]);

  useEffect(() => { charger(); }, [charger]);

  if (erreur) return <div style={{ color: THEME.red, padding: 20 }}>{erreur}</div>;
  if (!d) return <div style={{ color: THEME.textMuted, padding: 20 }}>Chargement…</div>;

  const s = d.sanction;
  const nv = NIVEAU_STYLE[s.niveau] || NIVEAU_STYLE.kick;
  const st = STATUT_STYLE[s.statut] || STATUT_STYLE.expiree;
  const libelleMotif = ((bareme && bareme.bareme[s.niveau]) || {})[s.motif]
    ? bareme.bareme[s.niveau][s.motif].label : s.motif;

  const agir = async (fn) => {
    try { await fn(); setAction(null); setMotifAction(""); setNouvelleDuree(""); await charger(); if (onChange) onChange(); }
    catch (e) { setErreur(e.message); }
  };

  const carte = { background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 16, marginBottom: 12 };
  const lbl = { color: THEME.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };
  const btn = (couleur, fond) => ({ background: fond, border: `1px solid ${couleur}`, color: couleur, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" });

  return (
    <div>
      <button onClick={onRetour} style={{ ...btn(THEME.textMuted, "transparent"), marginBottom: 14 }}>← Retour</button>

      <div style={carte}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <span style={{ background: nv.bg, color: nv.color, borderRadius: 6, padding: "3px 10px", fontSize: 13, fontWeight: 800 }}>
            {nv.icone} {nv.label}
          </span>
          <span style={{ color: THEME.text, fontSize: 20, fontWeight: 800 }}>{s.pseudo}</span>
          <span style={{ background: st.bg, color: st.color, borderRadius: 6, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>{st.label}</span>
          {s.hors_bareme === 1 && (
            <span style={{ background: THEME.amberDim, color: THEME.amber, borderRadius: 6, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>
              hors barème
            </span>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div><div style={lbl}>Motif</div><div style={{ color: THEME.text, fontSize: 13 }}>{libelleMotif}</div></div>
          <div><div style={lbl}>Durée</div><div style={{ color: THEME.text, fontSize: 13 }}>{dureeLisible(s.duree_min)}</div></div>
          <div><div style={lbl}>Posée le</div><div style={{ color: THEME.text, fontSize: 13 }}>{dateCourte(s.created_at)}</div></div>
          <div><div style={lbl}>Expire</div><div style={{ color: THEME.text, fontSize: 13 }}>{s.expire_le ? `${dateCourte(s.expire_le)} (${tempsRestant(s.expire_le)})` : "—"}</div></div>
          <div><div style={lbl}>Par</div><div style={{ color: THEME.text, fontSize: 13 }}>{s.moderateur_pseudo} <span style={{ color: THEME.textDim }}>· {GRADE_LABEL[s.moderateur_grade]}</span></div></div>
          {s.palier != null && <div><div style={lbl}>Palier</div><div style={{ color: THEME.text, fontSize: 13 }}>{s.palier}<sup>e</sup> occurrence</div></div>}
        </div>
      </div>

      <div style={carte}>
        <div style={lbl}>Raison publique — ce que le joueur a lu</div>
        <div style={{ color: THEME.text, fontSize: 14, fontStyle: "italic" }}>« {s.raison_publique || "—"} »</div>
      </div>

      <div style={carte}>
        <div style={{ ...lbl, marginBottom: 8 }}>Note interne — jamais visible du joueur</div>
        {d.droits.documenter ? (
          <>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={5}
              style={{ width: "100%", background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: 10, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
            <button onClick={() => agir(() => api.modifier(s.id, { note_interne: note }))}
              style={{ ...btn(THEME.accent, THEME.accentDim), marginTop: 8 }}>Enregistrer la note</button>
          </>
        ) : (
          <div style={{ color: THEME.text, fontSize: 13, whiteSpace: "pre-wrap" }}>{s.note_interne || "—"}</div>
        )}
      </div>

      {d.droits.agir && s.statut === "active" && (
        <div style={{ ...carte, borderColor: THEME.amber }}>
          <div style={{ ...lbl, color: THEME.amber, marginBottom: 8 }}>Agir sur la sanction — admin et fondateur</div>
          {!action ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setAction("lever")} style={btn(THEME.emerald, THEME.emeraldDim)}>Lever la sanction</button>
              {s.niveau !== "ban_def" && (
                <button onClick={() => setAction("duree")} style={btn(THEME.amber, THEME.amberDim)}>Modifier la durée</button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {action === "duree" && (
                <input type="number" min="0" placeholder="Nouvelle durée en minutes"
                  value={nouvelleDuree} onChange={(e) => setNouvelleDuree(e.target.value)}
                  style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "8px 10px", fontSize: 13 }} />
              )}
              <textarea rows={2} placeholder="Motif — obligatoire, il ira au journal"
                value={motifAction} onChange={(e) => setMotifAction(e.target.value)}
                style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: 10, fontSize: 13, fontFamily: "inherit" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button disabled={!motifAction.trim()}
                  onClick={() => agir(() => (action === "lever"
                    ? api.lever(s.id, motifAction)
                    : api.duree(s.id, parseInt(nouvelleDuree, 10), motifAction)))}
                  style={{ ...btn(THEME.emerald, THEME.emeraldDim), opacity: motifAction.trim() ? 1 : 0.4 }}>Confirmer</button>
                <button onClick={() => setAction(null)} style={btn(THEME.textMuted, "transparent")}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      )}

      {s.statut === "levee" && (
        <div style={{ ...carte, borderColor: THEME.emerald }}>
          <div style={{ ...lbl, color: THEME.emerald }}>Levée le {dateCourte(s.levee_le)} par {s.levee_par}</div>
          <div style={{ color: THEME.text, fontSize: 13 }}>{s.levee_motif}</div>
        </div>
      )}

      <div style={carte}>
        <div style={{ ...lbl, marginBottom: 10 }}>Journal — ouvert à tout le staff</div>
        {d.journal.length === 0 && <div style={{ color: THEME.textDim, fontSize: 13 }}>Rien pour l'instant.</div>}
        {d.journal.map((e) => (
          <div key={e.id} style={{ borderLeft: `2px solid ${THEME.border}`, paddingLeft: 10, marginBottom: 10 }}>
            <div style={{ color: THEME.textMuted, fontSize: 11 }}>
              {dateCourte(e.created_at)} · <b style={{ color: THEME.text }}>{e.auteur}</b> · {e.action}
            </div>
            {e.contenu && <div style={{ color: THEME.text, fontSize: 13, whiteSpace: "pre-wrap", marginTop: 2 }}>{e.contenu}</div>}
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input placeholder="Ajouter un commentaire…" value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && commentaire.trim()) agir(async () => { await api.commenter(s.id, commentaire); setCommentaire(""); }); }}
            style={{ flex: 1, background: THEME.surface, border: `1px solid ${THEME.border}`, color: THEME.text, borderRadius: 8, padding: "8px 10px", fontSize: 13 }} />
          <button disabled={!commentaire.trim()}
            onClick={() => agir(async () => { await api.commenter(s.id, commentaire); setCommentaire(""); })}
            style={{ ...btn(THEME.accent, THEME.accentDim), opacity: commentaire.trim() ? 1 : 0.4 }}>Commenter</button>
        </div>
      </div>
    </div>
  );
}
